import React, { useState, useEffect, useCallback, useRef } from 'react';
import { logementService } from '../services/logementService';
import { handleError } from '../utils/errorHandler';
import { useAuth } from '../contexts/AuthContext';
import { useLogementUpdates } from '../hooks/useWebSocket';
import Alert from './Alert';

const INITIAL_FILTERS = {
  statut: '',
  ville: '',
  type_auteur: '',
  auteur: '',
  skip: 0,
  limit: 10
};

const buildLogementParams = (filters) => {
  const params = {
    skip: filters.skip,
    limit: filters.limit
  };

  ['statut', 'ville', 'type_auteur', 'auteur'].forEach((key) => {
    const value = filters[key];
    if (value && String(value).trim()) {
      params[key] = String(value).trim();
    }
  });

  return params;
};

const LogementList = ({ onEdit, onView, onDataChange, refreshTrigger, loadingEdit = false }) => {
  const { user } = useAuth();
  const [logements, setLogements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);
  const appliedFiltersRef = useRef(INITIAL_FILTERS);
  const requestSequenceRef = useRef(0);
  const hasLoadedRef = useRef(false);

  const statutColors = {
    disponible: 'bg-green-100 text-green-800',
    occupe: 'bg-red-100 text-red-800',
    maintenance: 'bg-yellow-100 text-yellow-800'
  };

  const loadLogements = useCallback(async (requestFilters, signal) => {
    const requestId = ++requestSequenceRef.current;
    const isInitialLoad = !hasLoadedRef.current;

    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      const data = await logementService.getLogements(
        buildLogementParams(requestFilters),
        signal ? { signal } : {}
      );

      if (requestId === requestSequenceRef.current) {
        setLogements(data);
        setError(null);
      }
    } catch (err) {
      const requestWasCancelled = err.code === 'ERR_CANCELED' || err.name === 'CanceledError';
      if (!requestWasCancelled && requestId === requestSequenceRef.current) {
        const errorMsg = 'Erreur lors du chargement des logements';
        setError(errorMsg);
        handleError(err, 'Chargement des logements', errorMsg);
      }
    } finally {
      if (requestId === requestSequenceRef.current) {
        hasLoadedRef.current = true;
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
  }, [appliedFilters]);

  const villeFilter = filters.ville;
  const auteurFilter = filters.auteur;

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setAppliedFilters((currentFilters) => {
        if (
          currentFilters.ville === villeFilter &&
          currentFilters.auteur === auteurFilter &&
          currentFilters.skip === 0
        ) {
          return currentFilters;
        }

        return {
          ...currentFilters,
          ville: villeFilter,
          auteur: auteurFilter,
          skip: 0
        };
      });
    }, 450);

    return () => window.clearTimeout(timeoutId);
  }, [villeFilter, auteurFilter]);

  useEffect(() => {
    const controller = new AbortController();
    loadLogements(appliedFilters, controller.signal);

    return () => controller.abort();
  }, [appliedFilters, loadLogements]);

  // Écouter les mises à jour externes (après création/modification/suppression)
  useEffect(() => {
    if (refreshTrigger > 0) {
      loadLogements(appliedFiltersRef.current);
    }
  }, [refreshTrigger, loadLogements]);

  // Écouter les mises à jour WebSocket des logements
  const handleLogementUpdate = useCallback(() => {
    console.log('🔔 Mise à jour de la liste logements suite à changement');
    loadLogements(appliedFiltersRef.current);
  }, [loadLogements]);

  useLogementUpdates(handleLogementUpdate);

  const handleFilterChange = (key, value) => {
    const isPagination = key === 'skip';
    const isDebouncedField = key === 'ville' || key === 'auteur';
    const nextSkip = isPagination ? value : 0;

    setFilters((currentFilters) => ({
      ...currentFilters,
      [key]: value,
      skip: nextSkip
    }));

    if (!isDebouncedField) {
      setAppliedFilters((currentFilters) => ({
        ...currentFilters,
        [key]: value,
        skip: nextSkip
      }));
    }
  };

  const handleFiltersReset = () => {
    setFilters(INITIAL_FILTERS);
    setAppliedFilters(INITIAL_FILTERS);
  };


  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce logement ?')) {
      return;
    }

    try {
      await logementService.deleteLogement(id);
      await loadLogements(appliedFiltersRef.current);
      // Notifier le parent que les données ont changé
      if (onDataChange) {
        onDataChange();
      }
    } catch (err) {
      const errorMsg = 'Erreur lors de la suppression';
      setError(errorMsg);
      handleError(err, 'Suppression logement', errorMsg);
    }
  };

  const handleChangeStatut = async (id, nouveauStatut) => {
    try {
      await logementService.changeStatutLogement(id, nouveauStatut);
      await loadLogements(appliedFiltersRef.current);
      // Notifier le parent que les données ont changé
      if (onDataChange) {
        onDataChange();
      }
    } catch (err) {
      const errorMsg = 'Erreur lors du changement de statut';
      setError(errorMsg);
      handleError(err, 'Changement statut', errorMsg);
    }
  };

  return (
    <div className="space-y-6">
      {/* Filtres */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex justify-end min-h-5 mb-2">
          {refreshing && (
            <div role="status" className="flex items-center gap-2 text-sm text-blue-600">
              <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></span>
              Actualisation...
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <div>
            <label htmlFor="logement-filter-statut" className="block text-sm font-medium text-gray-700 mb-2">
              Filtrer par statut
            </label>
            <select
              id="logement-filter-statut"
              value={filters.statut}
              onChange={(e) => handleFilterChange('statut', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tous les statuts</option>
              <option value="disponible">Disponible</option>
              <option value="occupe">Occupé</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </div>
          <div>
            <label htmlFor="logement-filter-ville" className="block text-sm font-medium text-gray-700 mb-2">
              Filtrer par ville
            </label>
            <input
              id="logement-filter-ville"
              type="text"
              value={filters.ville}
              onChange={(e) => handleFilterChange('ville', e.target.value)}
              placeholder="Rechercher une ville..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label htmlFor="logement-filter-auteur" className="block text-sm font-medium text-gray-700 mb-2">
              Auteur du logement
            </label>
            <input
              id="logement-filter-auteur"
              type="text"
              value={filters.auteur}
              onChange={(e) => handleFilterChange('auteur', e.target.value)}
              placeholder="Nom, prénom ou email..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label htmlFor="logement-filter-source" className="block text-sm font-medium text-gray-700 mb-2">
              Source logement
            </label>
            <select
              id="logement-filter-source"
              value={filters.type_auteur}
              onChange={(e) => handleFilterChange('type_auteur', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Toutes les sources</option>
              <option value="automation">🏢 Logements partenaire</option>
              <option value="manuel">🏠 Nos logements</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={handleFiltersReset}
              className="w-full px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Réinitialiser tous les filtres
            </button>
          </div>
        </div>
      </div>

      {/* Message d'erreur */}
      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => setError(null)}
        />
      )}

      {/* Liste des logements */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex flex-col justify-center items-center h-64 text-gray-500" role="status">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            <p className="mt-3">Chargement des logements...</p>
          </div>
        ) : logements.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Aucun logement trouvé</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Logement
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Localisation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prix total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logements.map((logement) => (
                  <tr key={logement.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {logement.titre}
                        </div>
                        <div className="text-sm text-gray-500">
                          {logement.adresse}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm text-gray-900">{logement.ville}</div>
                        <div className="text-xs text-gray-500">{logement.code_postal}, {logement.pays}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        <div className="font-semibold">{logement.montant_total}€</div>
                        <div className="text-xs text-gray-500">
                          Loyer: {logement.loyer}€{logement.montant_charges > 0 ? ` + Charges: ${logement.montant_charges}€` : ''}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statutColors[logement.statut]}`}>
                        {logement.statut}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => onView(logement)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Voir
                      </button>
                      <button
                        onClick={() => onEdit(logement)}
                        disabled={loadingEdit}
                        className="text-indigo-600 hover:text-indigo-900 disabled:text-gray-400 disabled:cursor-not-allowed"
                      >
                        {loadingEdit ? 'Chargement...' : 'Modifier'}
                      </button>
                      <select
                        value={logement.statut}
                        onChange={(e) => handleChangeStatut(logement.id, e.target.value)}
                        className="text-sm border border-gray-300 rounded px-2 py-1"
                      >
                        <option value="disponible">Disponible</option>
                        <option value="occupe">Occupé</option>
                        <option value="maintenance">Maintenance</option>
                      </select>
                      {/* Bouton Supprimer - visible uniquement pour admin-generale et bailleur (PAS pour agent-boaz) */}
                      {(user?.role === 'admin-generale' || user?.role === 'bailleur') && (
                        <button
                          onClick={() => handleDelete(logement.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Supprimer
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination responsive */}
      {!loading && (
      <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
        {/* Version desktop */}
        <div className="hidden md:flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleFilterChange('skip', Math.max(0, filters.skip - filters.limit))}
              disabled={filters.skip === 0}
              className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
              </svg>
              Précédent
            </button>
            <button
              onClick={() => handleFilterChange('skip', filters.skip + filters.limit)}
              disabled={logements.length < filters.limit}
              className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
            >
              Suivant
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </button>
          </div>

          {/* Informations de pagination */}
          <div className="flex items-center text-sm text-gray-700">
            <span className="mr-2">
              Affichage de {filters.skip + 1} à {filters.skip + logements.length}
            </span>
            {logements.length > 0 && (
              <span className="text-gray-500">
                | Page {Math.floor(filters.skip / filters.limit) + 1}
              </span>
            )}
            {logements.length === filters.limit && (
              <span className="ml-2 text-blue-600 text-xs">
                (Il peut y avoir plus de résultats)
              </span>
            )}
          </div>

          {/* Sélecteur de limite */}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-700">Afficher:</label>
            <select
              value={filters.limit}
              onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
              className="text-sm border border-gray-300 rounded px-2 py-1"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
            <span className="text-sm text-gray-700">par page</span>
          </div>
        </div>

        {/* Version mobile/tablette */}
        <div className="md:hidden space-y-3">
          {/* Ligne 1: Boutons navigation */}
          <div className="flex justify-between items-center">
            <button
              onClick={() => handleFilterChange('skip', Math.max(0, filters.skip - filters.limit))}
              disabled={filters.skip === 0}
              className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
              </svg>
              Précédent
            </button>
            <button
              onClick={() => handleFilterChange('skip', filters.skip + filters.limit)}
              disabled={logements.length < filters.limit}
              className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
            >
              Suivant
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </button>
          </div>

          {/* Ligne 2: Informations de pagination */}
          <div className="text-center">
            <div className="text-sm text-gray-700">
              <span>Affichage de {filters.skip + 1} à {filters.skip + logements.length}</span>
              {logements.length > 0 && (
                <span className="text-gray-500 ml-2">
                  • Page {Math.floor(filters.skip / filters.limit) + 1}
                </span>
              )}
            </div>
            {logements.length === filters.limit && (
              <div className="text-blue-600 text-xs mt-1">
                (Il peut y avoir plus de résultats)
              </div>
            )}
          </div>

          {/* Ligne 3: Sélecteur de limite */}
          <div className="flex justify-center items-center space-x-2">
            <label className="text-sm text-gray-700">Afficher:</label>
            <select
              value={filters.limit}
              onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
              className="text-sm border border-gray-300 rounded px-2 py-1"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
            <span className="text-sm text-gray-700">par page</span>
          </div>
        </div>

        {/* Indicateur de statut de pagination */}
        {logements.length === 0 && !loading && (
          <div className="text-center text-gray-500 mt-4">
            <p>Aucun logement trouvé avec les filtres actuels</p>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default LogementList;
